// Menús (autenticacion.menus). Sistemas → Gestión de Menús → Menú.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, Switch, auth, fieldCls } from './shared';

interface Menu { id: string; nombre: string; icon: string | null; descripcion: string | null; url: string | null; orden: number; activo: boolean }
const vacio = { nombre: '', icon: '', descripcion: '', url: '', orden: 0, activo: true };

export function Menus() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Menu[]>({ queryKey: ['adm-menus'], queryFn: () => auth.list<Menu>('menus', '?select=*&order=orden') });
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const [fNombre, setFNombre] = useState('');
  const [fEstado, setFEstado] = useState('todos');
  const filtrados = useMemo(() => rows.filter((m) =>
    (!fNombre.trim() || m.nombre.toLowerCase().includes(fNombre.trim().toLowerCase())) &&
    (fEstado === 'todos' || (fEstado === 'activo' ? m.activo : !m.activo))
  ), [rows, fNombre, fEstado]);

  const abrir = (m?: Menu) => {
    if (m) { setEditId(m.id); setForm({ nombre: m.nombre, icon: m.icon ?? '', descripcion: m.descripcion ?? '', url: m.url ?? '', orden: m.orden, activo: m.activo }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };
  const guardar = useMutation({
    mutationFn: () => {
      const body = { nombre: form.nombre, icon: form.icon || null, descripcion: form.descripcion || null, url: form.url || null, orden: form.orden, activo: form.activo, updated_at: new Date().toISOString() };
      return editId ? auth.update('menus', editId, body) : auth.create('menus', body);
    },
    onSuccess: () => { toast.success('Menú guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-menus'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('menus', id),
    onSuccess: () => { toast.success('Menú eliminado'); void qc.invalidateQueries({ queryKey: ['adm-menus'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Menús</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo menú</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input className={`${fieldCls} w-64`} placeholder="Filtrar por nombre…" value={fNombre} onChange={(e) => setFNombre(e.target.value)} />
        <select className={`${fieldCls} w-40`} value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
          <option value="todos">Todos los estados</option><option value="activo">Activos</option><option value="inactivo">Inactivos</option>
        </select>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Menús ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Icono</th><th className="py-2 pr-3">Orden</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">Sin menús</td></tr>}
              {filtrados.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{m.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{m.icon || '—'}</td>
                  <td className="py-2 pr-3 font-mono">{m.orden}</td>
                  <td className="py-2 pr-3">{m.activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(m)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Eliminar" onClick={async () => { if (await dlg.confirmar(`¿Eliminar "${m.nombre}"?`, { peligro: true })) eliminar.mutate(m.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar menú' : 'Nuevo menú'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Nombre *"><input className={fieldCls} maxLength={15} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Icono (lucide)"><input className={fieldCls} value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} /></Campo>
              <Campo label="Orden"><input type="number" className={fieldCls} value={form.orden} onChange={(e) => setForm({ ...form, orden: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="URL"><input className={fieldCls} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} /></Campo>
            <Campo label="Descripción"><textarea className={`${fieldCls} h-16`} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
